import { useState } from 'react';
import { motion } from 'framer-motion';
import API from '../api';
import { useNavigate, Link } from 'react-router-dom';

export default function Register() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isRegistered, setIsRegistered] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  const register = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      await API.post('/register', { email, password });
      setIsRegistered(true);
    } catch (error) {
      console.error('Registration error:', error.response || error);
      alert(error.response?.data?.detail || 'Registration failed');
    } finally {
      setIsLoading(false);
    }
  };

  if (isRegistered) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 50 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, ease: 'easeOut' }}
        style={{ 
          minHeight: '100vh', 
          background: '#111111',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '2rem'
        }}
      >
        {/* Animated Background Particles */}
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          pointerEvents: 'none',
          zIndex: 1
        }}>
          {[...Array(10)].map((_, i) => (
            <motion.div
              key={i}
              style={{
                position: 'absolute',
                width: '2px',
                height: '2px',
                background: '#7CFC00',
                borderRadius: '50%',
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
                opacity: 0.6,
              }}
              animate={{
                y: [0, -30, 0],
                opacity: [0.3, 0.8, 0.3],
              }}
              transition={{
                duration: Math.random() * 3 + 2,
                repeat: Infinity,
                delay: Math.random() * 2,
                ease: 'easeInOut',
              }}
            />
          ))}
        </div>

        <motion.div 
          style={{ 
            maxWidth: '500px', 
            width: '100%',
            padding: '3rem', 
            textAlign: 'center',
            background: 'rgba(255, 255, 255, 0.02)',
            borderRadius: '20px',
            border: '1px solid rgba(124, 252, 0, 0.2)',
            backdropFilter: 'blur(20px)',
            boxShadow: '0 0 30px rgba(124, 252, 0, 0.1)',
            position: 'relative',
            zIndex: 10
          }}
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 0.6, delay: 0.2, ease: 'easeOut' }}
        >
          <motion.div 
            style={{ fontSize: '4rem', marginBottom: '1rem' }}
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ duration: 0.6, delay: 0.4, type: 'spring' }}
          >
            ✉️
          </motion.div>
          
          <motion.h2 
            style={{ fontSize: '2rem', marginBottom: '1rem', fontFamily: 'Poppins, sans-serif', fontWeight: '600', color: 'white' }}
            initial={{ y: -20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.6 }}
          >
            Check Your Email!
          </motion.h2>
          
          <motion.p 
            style={{ fontSize: '1.1rem', color: '#e0e0e0', lineHeight: '1.6', marginBottom: '2rem' }}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.8 }}
          >
            We've sent a verification link to <strong style={{ color: '#7CFC00' }}>{email}</strong>
          </motion.p>
          
          <motion.p 
            style={{ fontSize: '1rem', color: '#b0b0b0', lineHeight: '1.5', marginBottom: '2rem' }}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 1.0 }}
          >
            Click the link in your email to verify your account and start using StatPulseAi!
          </motion.p>
          
          <motion.div
            style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 1.2 }}
          >
            <motion.button
              onClick={() => navigate('/login')}
              style={{
                backgroundColor: '#7CFC00',
                color: '#000',
                padding: '0.8rem 2rem',
                borderRadius: '8px',
                border: 'none',
                cursor: 'pointer',
                fontSize: '1rem',
                fontWeight: 'bold'
              }}
              whileHover={{ scale: 1.02, boxShadow: '0 0 20px rgba(124, 252, 0, 0.4)' }}
              whileTap={{ scale: 0.98 }}
            >
              Go to Login
            </motion.button>
            
            <motion.button
              onClick={() => setIsRegistered(false)}
              style={{
                backgroundColor: 'transparent',
                color: '#7CFC00',
                padding: '0.6rem 1.5rem',
                borderRadius: '8px',
                border: '1px solid #7CFC00',
                cursor: 'pointer',
                fontSize: '1rem'
              }}
              whileHover={{ scale: 1.02, boxShadow: '0 0 15px rgba(124, 252, 0, 0.3)' }}
              whileTap={{ scale: 0.98 }}
            >
              Back to Register
            </motion.button>
          </motion.div>
        </motion.div>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 50 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.8, ease: 'easeOut' }}
      style={{ 
        minHeight: '100vh', 
        background: '#111111',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '2rem'
      }}
    >
      {/* Animated Background Particles */}
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        pointerEvents: 'none',
        zIndex: 1
      }}>
        {[...Array(10)].map((_, i) => (
          <motion.div
            key={i}
            style={{
              position: 'absolute',
              width: '2px',
              height: '2px',
              background: '#7CFC00',
              borderRadius: '50%',
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              opacity: 0.6,
            }}
            animate={{
              y: [0, -30, 0],
              opacity: [0.3, 0.8, 0.3],
            }}
            transition={{
              duration: Math.random() * 3 + 2,
              repeat: Infinity,
              delay: Math.random() * 2,
              ease: 'easeInOut',
            }}
          />
        ))}
      </div>

      <motion.form 
        onSubmit={register} 
        style={{ 
          maxWidth: '450px', 
          width: '100%',
          padding: '2.5rem', 
          textAlign: 'center',
          background: 'rgba(255, 255, 255, 0.02)',
          borderRadius: '20px',
          border: '1px solid rgba(124, 252, 0, 0.2)',
          backdropFilter: 'blur(20px)',
          boxShadow: '0 0 30px rgba(124, 252, 0, 0.1)',
          position: 'relative',
          zIndex: 10
        }}
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ duration: 0.6, delay: 0.2, ease: 'easeOut' }}
      >
        <motion.div 
          className="title"
          initial={{ y: -20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.6, delay: 0.4 }}
        >
          <img src="/logo.png" className="logo-rect" alt="StatPulseAI Logo" style={{ height: '100px', marginBottom: '0.1rem' }} />
        </motion.div>
        
        <motion.h2 
          style={{ fontSize: '2rem', marginBottom: '1.2rem', fontFamily: 'Poppins, sans-serif', fontWeight: '600', color: 'white' }}
          initial={{ y: -20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.6, delay: 0.6 }}
        >
          Register
        </motion.h2>
        
        <motion.input 
          placeholder="Email" 
          onChange={e => setEmail(e.target.value)} 
          style={{ width: '100%', padding: '0.8rem', marginBottom: '0.8rem', fontSize: '1rem', borderRadius: '8px', border: '1px solid #ccc', boxSizing: 'border-box', background: 'rgba(255, 255, 255, 0.9)', color: '#333' }}
          required
          initial={{ x: -50, opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          transition={{ duration: 0.6, delay: 0.8 }}
        />
        
        <motion.input 
          placeholder="Password" 
          type="password" 
          onChange={e => setPassword(e.target.value)} 
          style={{ width: '100%', padding: '0.8rem', marginBottom: '0.8rem', fontSize: '1rem', borderRadius: '8px', border: '1px solid #ccc', boxSizing: 'border-box', background: 'rgba(255, 255, 255, 0.9)', color: '#333' }}
          required
          initial={{ x: -50, opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          transition={{ duration: 0.6, delay: 1.0 }}
        />
        
        <motion.button 
          type="submit"
          disabled={isLoading}
          style={{
            width: '100%',
            backgroundColor: isLoading ? '#666' : '#7CFC00',
            color: '#000',
            fontWeight: 'bold',
            padding: '0.8rem',
            borderRadius: '8px',
            border: 'none',
            cursor: isLoading ? 'not-allowed' : 'pointer',
            fontSize: '1.1rem',
            marginBottom: '0.8rem'
          }}
          initial={{ y: 30, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.6, delay: 1.2 }}
          whileHover={!isLoading ? { scale: 1.02, boxShadow: '0 0 20px rgba(124, 252, 0, 0.4)' } : {}}
          whileTap={!isLoading ? { scale: 0.98 } : {}}
        >
          {isLoading ? 'Creating Account...' : 'Register'}
        </motion.button>
        
        <motion.div 
          style={{ marginBottom: '0.8rem' }}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6, delay: 1.4 }}
        >
          <Link to="/login" style={{ color: '#7CFC00', fontSize: '1rem', textDecoration: 'none' }}>Already have an account? Login</Link>
        </motion.div>
        
        {/* Go Back Button */}
        <motion.button
          type="button"
          onClick={() => navigate('/')}
          style={{
            backgroundColor: '#001c3d',
            color: '#7CFC00',
            padding: '0.6rem 1.5rem',
            borderRadius: '8px',
            border: 'none',
            cursor: 'pointer',
            fontSize: '1rem'
          }}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6, delay: 1.6 }}
          whileHover={{ scale: 1.05, boxShadow: '0 0 15px rgba(0, 28, 61, 0.6)' }}
          whileTap={{ scale: 0.95 }}
        >
          Main Page
        </motion.button>
      </motion.form>
    </motion.div>
  );
}
